<template>
  <div class="bg-white min-h-screen">
    <!-- Hero Banner Section -->
    <section class="bg-gradient-to-br from-[#CB2530] to-[#B41923] relative overflow-hidden pt-24 lg:pt-32 pb-12 lg:pb-16 min-h-[320px] lg:min-h-[400px] flex items-center">
      <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center relative z-10 w-full">
        <h1 class="text-4xl md:text-5xl text-white mb-6 leading-tight">{{ contactConfigs.contact_banner_title || $t('common.contact.title') }}</h1>
        <div class="max-w-3xl mx-auto">
          <p class="text-xl text-white/85 leading-relaxed whitespace-pre-wrap">
            {{ contactConfigs.contact_banner_desc }}
          </p>
        </div>
      </div>
    </section>

    <!-- Contact Information Cards -->
    <section class="py-20 bg-white">
      <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          <div v-for="(info, index) in contactCards" :key="index" 
               class="bg-gradient-to-br from-[#FDF3F4] to-[#FBEEEF] rounded-xl p-4 lg:p-6 border border-[#F2D6D8] hover:shadow-lg transition-all group flex flex-col items-center justify-center min-h-[180px]"
          >
            <div class="text-[#C6202B] mb-4 flex items-center justify-center transition-transform duration-300 group-hover:scale-110">
              <img v-if="isExternalUrl(info.iconName)" :src="processImageUrl(info.iconName)" class="w-11 h-11 object-contain" />
              <i v-else :class="getIconClass(info.iconName)" class="text-4xl"></i>
            </div>
            <div class="flex flex-col items-center justify-center w-full">
              <h3 class="text-slate-900 mb-3 font-medium text-lg leading-none">{{ info.title }}</h3>
              <p class="text-[#C6202B] font-normal text-base leading-relaxed tracking-tight text-center w-full px-2">{{ info.content }}</p>
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- Inquiry Form Section -->
    <section class="py-20 bg-slate-50">
      <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div class="text-center mb-12 space-y-4">
          <h2 class="text-4xl md:text-5xl text-slate-900 mb-4 tracking-tight font-medium">{{ $t('common.contact.title') }}</h2>
          <p class="text-xl text-slate-600 leading-relaxed">{{ $t('common.contact.subtitle') }}</p>
        </div>

        <div class="bg-white p-8 lg:p-12 rounded-2xl border border-slate-200 shadow-lg">
          <form @submit.prevent="submitForm" class="space-y-6">
            <div class="grid grid-cols-1 md:grid-cols-2 gap-6">
              <!-- Company Name -->
              <div class="space-y-2">
                <label class="block text-slate-700 font-medium ml-1">{{ $t('common.contact.form.companyName') }} <span class="text-red-500">*</span></label>
                <input v-model="form.companyName" type="text" required :placeholder="$t('common.contact.form.companyNamePlaceholder')" class="w-full px-4 py-3 bg-slate-50 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#C6202B] focus:border-transparent transition-all text-slate-900">
              </div>
              <!-- Contact Person -->
              <div class="space-y-2">
                <label class="block text-slate-700 font-medium ml-1">{{ $t('common.contact.form.contactPerson') }} <span class="text-red-500">*</span></label>
                <input v-model="form.contactPerson" type="text" required :placeholder="$t('common.contact.form.contactPersonPlaceholder')" class="w-full px-4 py-3 bg-slate-50 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#C6202B] focus:border-transparent transition-all text-slate-900">
              </div>
              <!-- Phone -->
              <div class="space-y-2">
                <label class="block text-slate-700 font-medium ml-1">{{ $t('common.contact.form.phone') }} <span class="text-red-500">*</span></label>
                <input v-model="form.phone" type="tel" required :placeholder="$t('common.contact.form.phonePlaceholder')" class="w-full px-4 py-3 bg-slate-50 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#C6202B] focus:border-transparent transition-all text-slate-900">
              </div>
              <!-- Email -->
              <div class="space-y-2">
                <label class="block text-slate-700 font-medium ml-1">{{ $t('common.contact.form.email') }} <span class="text-red-500">*</span></label>
                <input v-model="form.email" type="email" required :placeholder="$t('common.contact.form.emailPlaceholder')" class="w-full px-4 py-3 bg-slate-50 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#C6202B] focus:border-transparent transition-all text-slate-900">
              </div>
              <!-- Model Request -->
              <div class="space-y-2">
                <label class="block text-slate-700 font-medium ml-1">{{ $t('common.contact.form.modelRequest') }} <span class="text-red-500">*</span></label>
                <input v-model="form.modelRequest" type="text" required :placeholder="$t('common.contact.form.modelRequestPlaceholder')" class="w-full px-4 py-3 bg-slate-50 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#C6202B] focus:border-transparent transition-all text-slate-900">
              </div>
              <!-- Diameter Request -->
              <div class="space-y-2">
                <label class="block text-slate-700 font-medium ml-1">{{ $t('common.contact.form.specsRequest') }} <span class="text-red-500">*</span></label>
                <input v-model="form.specsRequest" type="text" required :placeholder="$t('common.contact.form.specsPlaceholder')" class="w-full px-4 py-3 bg-slate-50 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#C6202B] focus:border-transparent transition-all text-slate-900">
              </div>
            </div>

            <!-- Detailed Requirement -->
            <div class="space-y-2">
              <label class="block text-slate-700 font-medium ml-1">{{ $t('common.contact.form.detail') }}</label>
              <textarea v-model="form.detailRequirement" rows="6" :placeholder="$t('common.contact.form.detailPlaceholder')" class="w-full px-4 py-3 bg-slate-50 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#C6202B] focus:border-transparent transition-all resize-none text-slate-900"></textarea>
            </div>

            <!-- Privacy Policy -->
            <div class="flex items-start gap-3 p-4 bg-[#FDF3F4] rounded-lg border border-[#F2D6D8]">
              <svg class="w-5 h-5 text-[#C6202B] flex-shrink-0 mt-0.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="m22 7-8.97 5.7a1.94 1.94 0 0 1-2.06 0L2 7"/><rect width="20" height="16" x="2" y="4" rx="2"/></svg>
              <p class="text-sm text-slate-700 leading-relaxed">
                <strong class="font-bold">{{ $t('common.contact.form.privacyTitle') }}：</strong>{{ $t('common.contact.form.privacyText') }}
              </p>
            </div>

            <!-- Submit Button -->
            <button type="submit" :disabled="submitting" class="w-full py-4 bg-gradient-to-r from-[#D3262F] to-[#8E1219] hover:from-[#B41B25] hover:to-[#761016] text-white rounded-lg text-lg font-medium transition-all shadow-lg hover:shadow-xl flex items-center justify-center gap-2 disabled:opacity-50 active:scale-[0.98]">
              <svg class="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M22 2L11 13M22 2l-7 20-4-9-9-4 20-7z" stroke-linecap="round" stroke-linejoin="round"/></svg>
              <span>{{ submitting ? $t('common.contact.form.submitting') : $t('common.contact.form.submit') }}</span>
            </button>
          </form>
        </div>
      </div>
    </section>

    <!-- Map Section -->
    <section class="py-20 bg-white">
      <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <h2 class="text-3xl text-slate-900 mb-8 text-center font-medium">{{ $t('common.contact.factoryTitle') }}</h2>
        <div class="rounded-2xl overflow-hidden shadow-lg h-[400px] lg:h-[500px] relative bg-slate-100 group border border-slate-200">
          <!-- Factory Map Image -->
          <img :src="processImageUrl(contactConfigs.contact_map_url)" 
               class="w-full h-full object-cover transition-transform duration-1000 group-hover:scale-105" alt="Map">
          <div class="absolute inset-0 bg-black/5"></div>
          <div class="absolute bottom-10 left-1/2 -translate-x-1/2 bg-white px-8 py-4 rounded-2xl shadow-xl border border-slate-100 flex items-center space-x-4">
            <div class="w-10 h-10 rounded-full bg-[#C6202B]/10 flex items-center justify-center text-[#C6202B]">
              <svg class="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0118 0z" stroke-linecap="round" stroke-linejoin="round"/><circle cx="12" cy="10" r="3" stroke-linecap="round" stroke-linejoin="round"/></svg>
            </div>
            <div>
              <div class="text-slate-900 font-medium text-sm">{{ contactConfigs.contact_factory_name }}</div>
              <div class="text-slate-500 text-xs mt-0.5">{{ contactConfigs.contact_factory_address }}</div>
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- Bottom Vision -->
    <section class="py-20 bg-gradient-to-br from-[#150A0C] to-[#3A1116] text-white text-center relative overflow-hidden">
      <div class="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        <h2 class="text-3xl text-white mb-6 leading-relaxed font-medium">{{ contactConfigs.contact_vision_title }}</h2>
        <p class="text-xl text-slate-300 leading-relaxed opacity-90">{{ contactConfigs.contact_vision_desc }}</p>
      </div>
    </section>
  </div>
</template>

<script>
import { getPublicContactAll, submitPublicInquiry } from '@/api/contact'

export default {
  name: 'ContactView',
  props: {
    preloadedData: {
      type: Object,
      default: null
    }
  },
  data() {
    return {
      contactConfigs: {
        contact_banner_title: '',
        contact_banner_desc: '',
        contact_factory_name: '',
        contact_factory_address: '',
        contact_vision_title: '',
        contact_vision_desc: '',
        contact_map_url: ''
      },
      contactCards: [],
      form: {
        companyName: '',
        contactPerson: '',
        phone: '',
        email: '',
        modelRequest: '',
        specsRequest: '',
        quantity: '',
        application: '',
        detailRequirement: ''
      },
      submitting: false
    }
  },
  watch: {
    preloadedData: {
      handler(newVal) {
        if (newVal) {
          this.applyPreloadedData(newVal);
        }
      },
      immediate: true
    }
  },
  created() {
    this.fetchData()
    this.$root.$on('language-changed', this.fetchData)
  },
  beforeDestroy() {
    this.$root.$off('language-changed', this.fetchData)
  },
  methods: {
    applyPreloadedData(data) {
      // 更新配置
      Object.keys(this.contactConfigs).forEach(key => {
        if (data[key]) this.contactConfigs[key] = data[key]
      })
      // 更新联系卡片
      if (data.infoList && data.infoList.length > 0) {
        this.contactCards = data.infoList
      }
    },
    async fetchData() {
      try {
        const res = await getPublicContactAll()
        if (res.code === 200) {
          this.applyPreloadedData(res.data);
        }
      } catch (error) {
        console.error('Failed to fetch contact data:', error)
      }
    },
    async submitForm() {
      this.submitting = true
      try {
        const res = await submitPublicInquiry(this.form)
        if (res.code === 200) {
          alert(this.$t('common.contact.form.success'))
          this.resetForm()
        } else {
          alert(res.msg || this.$t('common.contact.form.fail'))
        }
      } catch (error) {
        alert(this.$t('common.contact.form.error'))
      } finally {
        this.submitting = false
      }
    },
    processImageUrl(url) {
      if (!url) return require('../assets/images/location.png')
      if (url.startsWith('http') || url.startsWith('blob:') || url.startsWith('data:')) return url
      const baseUrl = process.env.VUE_APP_BASE_API || 'http://localhost:8080'
      return baseUrl + url
    },
    isExternalUrl(url) {
      return url && (url.startsWith('/') || url.startsWith('http'))
    },
    resetForm() {
      this.form = {
        companyName: '',
        contactPerson: '',
        phone: '',
        email: '',
        modelRequest: '',
        specsRequest: '',
        quantity: '',
        application: '',
        detailRequirement: ''
      }
    },
    getIconClass(name) {
      const iconMap = {
        MapPin: 'el-icon-location',
        Phone: 'el-icon-phone',
        Mail: 'el-icon-message',
        User: 'el-icon-user',
        Location: 'el-icon-location-information'
      }
      return iconMap[name] || 'el-icon-location'
    }
  }
}
</script>

<style scoped>
.container-custom {
  max-width: 1280px;
  margin-left: auto;
  margin-right: auto;
  padding-left: 1.5rem;
  padding-right: 1.5rem;
}
</style>
